<script>
import baseMixin from '@/mixins/baseMixin'
import { Doughnut } from 'vue-chartjs/legacy'

import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
} from 'chart.js'
import OverviewStatsDto from '@/models/overview/overviewStatsDto'
import $i18n from '@/plugins/vue-i18n'

ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale, LinearScale)

export default {
  name: 'ClientGroupOverviewFillRateChart',
  components: { Doughnut },
  mixins: [baseMixin],
  props: {
    overviewStats: {
      type: OverviewStatsDto,
      default: null,
    },
    height: {
      type: Number,
      default: 120,
    },
    width: {
      type: Number,
      default: 120,
    },
    cutOut: {
      type: Number,
      default: 45,
    },
    hideSubtitle: {
      type: Boolean,
    },
    textSize: {
      type: String,
      default: '',
    },
  },
  data: () => {
    return {
      chartData: {},
      options: {
        plugins: {
          legend: {
            display: false,
          },
          backgroundCircle: {
            display: true,
            color: '#EFEFEF',
          },
          innerText: {
            display: true,
            displaySubtitle: true,
            textSize: '',
          },
        },
        cutout: 45,
        responsive: true,
        borderRadius: 60,
        borderColor: '#00000000',
        backgroundColor: '#000',
        chartArea: {
          backgroundColor: 'rgba(251, 85, 85, 0.4)',
        },
      },
      plugins: null,
    }
  },
  computed: {
    hasStatsObject() {
      return !!this.overviewStats
    },
  },
  watch: {
    overviewStats: {
      deep: true,
      async handler() {
        this.chartData = null
        await this.$nextTick()
        this.initChart()
      },
    },
  },
  mounted() {
    // HACK: Delay the chart being init to allow time for the fonts to be loaded
    // Fixes issue where fonts were displayed as a serif font
    // Sourced from: https://stackoverflow.com/a/76314280/2063537
    const initChart = this.initChart
    setTimeout(function() {
      initChart()
    }, 300)
  },
  methods: {
    initOptions() {
      const ONE_HUNDRED_PERCENT = 100

      if (this.overviewStats.fillRate === ONE_HUNDRED_PERCENT) {
        // Set border radius to 0 if fill rate is 100% to allow the chart to look
        // like a complete circle. With border radius the edges don't quite touch
        this.options.borderRadius = 0
      } else {
        this.options.borderRadius = 60
      }

      this.options.cutout = this.cutOut

      this.options.plugins.innerText.displaySubtitle = !this.hideSubtitle
      this.options.plugins.innerText.textSize = this.textSize
    },
    initChartData() {
      this.chartData = {
        labels: [
          this.$t('clientGroupOverview.stats.donut.filledLabel'),
          this.$t('clientGroupOverview.stats.donut.unfilledLabel'),
        ],
        datasets: [
          {
            backgroundColor: ['#00acef', '#00000000'],
            data: [this.overviewStats.filled, this.overviewStats.unfilled],
          },
        ],
      }
    },
    initChartPlugins() {
      const fill =
        this.overviewStats.fillRate < 0
          ? 'NA'
          : `${this.overviewStats.fillRate}%`

      /**
       * Plugin to apply a background to the empty space in our doughnut chart
       * @tutorial https://www.youtube.com/watch?v=SVWb61q-0e0
       */
      const backgroundCircle = {
        id: 'backgroundCircle',
        beforeDatasetsDraw(chart, args, pluginOptions) {
          const { ctx, config } = chart

          const isDisplayable =
            config?._config?.options?.plugins?.backgroundCircle?.display

          if (!isDisplayable) return

          const backgroundColor =
            config?._config?.options?.plugins?.backgroundCircle?.color ||
            '#EFEFEF'

          ctx.save()

          const xCoor = chart.getDatasetMeta(0).data[0].x
          const yCoor = chart.getDatasetMeta(0).data[0].y
          const innerRadius = chart.getDatasetMeta(0).data[0].innerRadius
          const outerRadius = chart.getDatasetMeta(0).data[0].outerRadius
          const angle = Math.PI / 180

          const width = outerRadius - innerRadius

          ctx.beginPath()
          ctx.lineWidth = width
          ctx.strokeStyle = backgroundColor
          ctx.arc(xCoor, yCoor, outerRadius - width / 2, 0, angle * 360, false)
          ctx.stroke()
        },
      }

      /**
       * Plugin the places text inside of the doughnut chart
       */
      const innerText = {
        id: 'innerText',
        beforeDraw: function(chart) {
          const { ctx, width, height, config } = chart

          const isDisplayable =
            config?._config?.options?.plugins?.innerText?.display

          const isSubtitleDisplayable =
            config?._config?.options?.plugins?.innerText?.displaySubtitle

          const textSize =
            config?._config?.options?.plugins?.innerText?.textSize

          if (!isDisplayable) return

          ctx.restore()

          // Main value
          let fontSize = textSize || `${(height / 90).toFixed(2)}em`
          let fontWeight = '600'
          const fontFamily = 'Poppins'
          ctx.font = `${fontWeight} ${fontSize} ${fontFamily}`

          ctx.textBaseline = 'middle'
          ctx.textAlign = 'start'

          const heightModifier = isSubtitleDisplayable ? 2.2 : 2

          const text = fill
          let textX = Math.round((width - ctx.measureText(text).width) / 2)
          let textY = height / heightModifier

          ctx.fillText(text, textX, textY)

          // Don't show sub heading if it'll be too small to see
          if (isSubtitleDisplayable) {
            // Subtitle
            const subtitle = $i18n.t('clientGroupOverview.stats.fillRateLabel') // Using this.$t() causes issues inside of plugins. Importing the vuei18n plugin instead

            fontSize = `${(height / 160).toFixed(2)}em`
            fontWeight = '400'
            ctx.font = `${fontWeight} ${fontSize} ${fontFamily}`

            ctx.textBaseline = 'middle'
            ctx.textAlign = 'start'

            textX = Math.round((width - ctx.measureText(subtitle).width) / 2)
            textY = height / 1.6

            ctx.fillText(subtitle, textX, textY)
          }

          ctx.save()
        },
      }

      this.plugins = [innerText, backgroundCircle]
    },
    initChart() {
      if (!this.hasStatsObject) return
      this.initOptions()
      this.initChartData()
      this.initChartPlugins()
    },
  },
}
</script>

<template>
  <section class="d-flex align-center flex-column">
    <Doughnut
      v-if="hasStatsObject && chartData"
      :width="width"
      :height="height"
      :chart-data="chartData"
      :chart-options="options"
      :plugins="plugins"
    />
    <div
      v-else
      class="d-flex flex-column justify-center text-center"
      style=" width: 150px;height: 150px;"
    >
      <p class="text-h6">
        <v-icon left color="primary">mdi-alert-circle</v-icon>
        {{ $t('clientGroupOverview.stats.donut.noDataText') }}
      </p>
    </div>
  </section>
</template>
